import { ElementType, useState } from 'react';

import { downloadBlob } from '@mint-lib/api-zod-support';
import {
  Doc as DocIcon,
  Document as DocumentIcon,
  Pdf as PdfIcon,
  Ppt as PptIcon,
  Xls as XlsIcon,
} from '@mint-lib/icons';
import {
  Card,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Chat } from '@mint-sdk/api-ai-assistant';

import { reportFileNameParser } from '../../../helpers/reportFileNameParser.js';
import {
  useReportPreviewData,
  useReportPreviewModal,
  type ReportPreviewData,
} from '../ReportPreview/useReportPreview.jsx';
import { DownloadButton } from './DownloadButton.jsx';

type Props = {
  chatId: string;
  filePath: string;
  pdfPath?: string | null;
};

const iconDict: Record<string, ElementType> = {
  docx: DocIcon,
  pdf: PdfIcon,
  xlsx: XlsIcon,
  pptx: PptIcon,
};

const DownloadReport = ({ chatId, filePath, pdfPath }: Props) => {
  const [cachedReport, setCachedReport] = useState<ReportPreviewData>({
    blobUrl: '',
    fileName: '',
    blob: undefined,
  });

  const { onOpen } = useReportPreviewModal();
  const { setReport } = useReportPreviewData();
  const { fileExtension, fileName } = reportFileNameParser(filePath);

  const KnownFileIcon = iconDict[fileExtension];

  const getBlob = async (path: string) => {
    return Chat.getReport({
      urlParams: { chatId },
      params: { s3Path: path },
    });
  };

  const handleDownloadFile = async () => {
    const blob = await getBlob(filePath);
    downloadBlob(blob, fileName);
  };

  const onReportPreviewOpen = async () => {
    if (!pdfPath) return;

    if (cachedReport.blobUrl) {
      setReport(cachedReport);
    } else {
      const [blobPDF, blobOriginal] = await Promise.all([
        getBlob(pdfPath),
        getBlob(filePath),
      ]);

      const blobUrl = URL.createObjectURL(blobPDF);

      const report = {
        blobUrl,
        fileName,
        blob: blobOriginal,
      };

      setCachedReport(report);
      setReport(report);
    }

    onOpen();
  };

  return (
    <Card
      variant="outlined"
      onClick={onReportPreviewOpen}
      className="cursor-pointer"
    >
      <div className="grid w-50.75 grid-cols-[auto_1fr_auto] items-center gap-2">
        {KnownFileIcon ? (
          <KnownFileIcon className="size-5" />
        ) : (
          <DocumentIcon className="size-5 fill-icon-primary" />
        )}

        <div
          className={typographyVariants({
            variant: 'bodyLong01',
            color: 'primary',
          })}
        >
          <TruncateText>
            <div className="max-w-32.5">{fileName}</div>
          </TruncateText>
        </div>

        <DownloadButton handleDownloadFile={handleDownloadFile} />
      </div>
    </Card>
  );
};

export { DownloadReport };
